import { PhasrSidePanelComponent } from '../component/PhasrSidePanelComponent';
import { PhasrSidePanelView } from '../view/PhasrSidePanelView';
import { templatePhasrSidePanel } from '../view/template';
import { BaseWebComponentController, IListenerConfig } from '../../framework/BaseWebComponentController';
import { LabelSwitchEvents } from '../../ui/gzLabelSwitch/types';
import {
  PAGE_LIMIT,
  PhasrSidePanelEvents,
  PhasrPanelComponentTypes,
  PhasrSidePanelViewData,
  SwitchToggleValues,
} from '../types/types';
import { GzSideMenuWebComponent } from '../../ui/gzSideMenu';
import { PhasrSidePanelRepository } from '../repository/PhasrSidePanelRepository';
import { InputEvents } from '../../ui/gzInput/types';
import { toggleVisibility } from '../../utils/htmlElement.utils';
import { isEmpty } from 'lodash';
import { PhasrListWebComponent } from '../../phasrRecommendations/phasrList';
import { PhasrApplyActionSchema } from '../../phasrApplyActionPanel/types';
import { translateFn } from '../../utils/translator.helper';
import { GzInputSearchWebComponent } from '../../ui/gzInput';
import { BehavioralProfileTypes } from '../../phasrRecommendations/phasrBehavioralProfilesComponent/types';
import {
  getPhasrCategoryDictionaryToPrefferedName,
} from '../../types/common/phasrTypes';

export class PhasrSidePanelController extends BaseWebComponentController {
  public component: PhasrSidePanelComponent;
  public view: PhasrSidePanelView;
  public dataRepository: PhasrSidePanelRepository;

  constructor(component: PhasrSidePanelComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: PhasrSidePanelView,
      viewTemplate: templatePhasrSidePanel,
      dataRepositoryConstructor: PhasrSidePanelRepository
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$labelSwitch,
        event: LabelSwitchEvents.CHANGE,
        callback: this.handleLabelSwitchChange
      },
      {
        element: this.view.dom.$backButton,
        event: 'click',
        callback: this.handleBackButton
      },
      {
        element: this.view.dom.$firstSearchInput,
        event: InputEvents.SEARCH,
        callback: async (ev: CustomEvent) => await this.handleSearchInput(ev, true)
      },
      {
        element: this.view.dom.$secondSearchInput,
        event: InputEvents.SEARCH,
        callback: async (ev: CustomEvent) => this.handleSearchInput(ev, false)
      },
      {
        element: this.view.dom.$firstActionButton,
        event: 'click',
        callback: this.handleActionButton
      },
      {
        element: this.view.dom.$firstList,
        event: PhasrSidePanelEvents.ViewSet,
        callback: async () => await this.setListItemsAccordingToComponentType()
      }
    ];
  }

  async getDataForView(): Promise<PhasrSidePanelViewData> {
    return {
      titleText: this.component.titleText,
      componentType: this.component.componentType,
      hasBackButton: this.component.hasBackButton
    };
  }

  private async setListItemsAccordingToComponentType(firstListSelected: boolean = true): Promise<void> {
    let filters: any[] = [];
    switch (this.component.componentType) {
      case PhasrPanelComponentTypes.BehavioralProfiles:
        filters = this.getBehavioralProfilesDefaultFilters();
        filters.push({
          property: 'notUsingSuchTools',
          value: firstListSelected
        });
        await this.setBehavioralProfilesListItems(filters, false, firstListSelected);
        break;
      case PhasrPanelComponentTypes.BehavioralProfilesNoToggle:
        filters = this.getBehavioralProfilesDefaultFilters();
        await this.setBehavioralProfilesListItems(filters, false, firstListSelected);
        break;
      case PhasrPanelComponentTypes.RestrictedBehavioralProfiles:
        this.view.dom.$firstActionButton.disabled = !(this.component.componentSpecificData.phasrUsable);
        filters = this.getBehavioralProfilesDefaultFilters();
        filters.push({
          property: 'mode',
          value: firstListSelected ? Number(BehavioralProfileTypes.AUTOPILOT) : Number(BehavioralProfileTypes.DIRECT_CONTROL)
        });
        await this.setBehavioralProfilesListItems(filters, false, firstListSelected);
        break;
      case PhasrPanelComponentTypes.RuleTriggers:
        const phasrList = this.view.dom.$firstList;
        await this.setRuleTriggersListItems(phasrList);
        break;
      default:
        return;
    }
  }

  private async setBehavioralProfilesListItems(
    filters: any[],
    onSearch: boolean = false,
    firstListSelected: boolean = true
  ): Promise<void> {
    const phasrList = firstListSelected ? this.view.dom.$firstList : this.view.dom.$secondList;
    phasrList.cleanup();
    phasrList.items = [];
    phasrList.isLoading = true;
    phasrList.items = await this.dataRepository.getBehavioralProfiles({
      page: 1,
      limit: PAGE_LIMIT,
      companyId: this.component.companyId,
      filter: filters,
    });

    // in case the sidePanel is closed, we need to stop the loading
    if (isEmpty(this.view.dom)) {
      return;
    }

    if (isEmpty(phasrList.items)) {
      toggleVisibility(this.view.dom.$searchContainer, onSearch);
      phasrList.emptyStateMessage = onSearch ? translateFn('No results found') : translateFn('No data available');
    } else {
        toggleVisibility(this.view.dom.$searchContainer, true);
        toggleVisibility(this.view.dom.$firstSearchInput, firstListSelected);
        toggleVisibility(this.view.dom.$secondSearchInput, !firstListSelected);
    }
    phasrList.isLoading = false;
    phasrList.loadMoreItems = () => {
      const page = Math.floor(phasrList.items.length / PAGE_LIMIT) + 1;
      if (page === 1 || phasrList.items.length % PAGE_LIMIT !== 0) {
        return Promise.resolve([]);
      }
      return this.dataRepository.getBehavioralProfiles({
        page: page,
        limit: PAGE_LIMIT,
        companyId: this.component.companyId,
        filter: filters,
      });
    };
  }

  private async setRuleTriggersListItems(
    phasrList: PhasrListWebComponent,
    searchValue?: string
  ): Promise<void> {
    phasrList.cleanup();
    phasrList.items = [];
    phasrList.isLoading = true;
    phasrList.items = await this.dataRepository.getRuleTriggers({
      page: 1,
      limit: PAGE_LIMIT,
      companyId: this.component.companyId,
      ruleId: this.component.componentSpecificData.ruleId,
      eventName: searchValue ?? null,
    });
    if (isEmpty(phasrList.items)) {
      phasrList.emptyStateMessage = translateFn('No results found') ;
    } else {
      toggleVisibility(this.view.dom.$firstSearchInput, true);
    }
    phasrList.isLoading = false;
    phasrList.loadMoreItems = () => {
      const page = Math.floor(phasrList.items.length / PAGE_LIMIT) + 1;
      if (page === 1 || phasrList.items.length % PAGE_LIMIT !== 0) {
        return Promise.resolve([]);
      }
      return this.dataRepository.getRuleTriggers({
        page: page,
        limit: PAGE_LIMIT,
        companyId: this.component.companyId,
        ruleId: this.component.componentSpecificData.ruleId,
        eventName: searchValue ?? null,
      });
    };
  }


  private async handleLabelSwitchChange(ev: CustomEvent): Promise<void> {
    this.view.dom.$labelSwitch.itemLeft.checked = !this.view.dom.$labelSwitch.itemLeft.checked;
    this.view.dom.$labelSwitch.itemRight.checked = !this.view.dom.$labelSwitch.itemRight.checked;
    switch (ev.detail.selectedValue) {
      case SwitchToggleValues.NotUsingTools:
      case SwitchToggleValues.Autopilot:
        this.toggleVisibilitySwitchView(true, this.view.dom.$firstSearchInput);
        break;
      case SwitchToggleValues.UsingTools:
      case SwitchToggleValues.DirectControl:
        this.toggleVisibilitySwitchView(false, this.view.dom.$secondSearchInput);
        if (isEmpty(this.view.dom.$secondList.items) && this.view.dom.$secondList.emptyStateMessage === '') {
          await this.setListItemsAccordingToComponentType(false);
        }
        break;
      default:
        break;
    }
  }

  private toggleVisibilitySwitchView(notUsingSuchTools: boolean, searchInput: GzInputSearchWebComponent): void {

    // handle lists visibility
    toggleVisibility(this.view.dom.$firstList, notUsingSuchTools);
    toggleVisibility(this.view.dom.$secondList, !notUsingSuchTools);

    // handle search container visibility
    toggleVisibility(this.view.dom.$searchContainer, true);
    toggleVisibility(this.view.dom.$firstSearchInput, notUsingSuchTools);
    toggleVisibility(this.view.dom.$secondSearchInput, !notUsingSuchTools);

    // in case no elements in list and no data on search input, hide search container
    const phasrList = notUsingSuchTools ? this.view.dom.$firstList : this.view.dom.$secondList;
    if (isEmpty(phasrList.items) && searchInput.value === '') {
      toggleVisibility(this.view.dom.$searchContainer, false);
    }

  }

  private handleBackButton(): void {
    const sidePanelParent = this.component.parentElement as GzSideMenuWebComponent;
    sidePanelParent.closeSideMenu(true);
  }

  private async handleSearchInput(ev: CustomEvent, isFirstSearchInput: boolean): Promise<void> {
    const searchedValue = ev.detail.sourceEvent;
    const phasrList = isFirstSearchInput ? this.view.dom.$firstList : this.view.dom.$secondList;
    phasrList.isLoading = true;
    phasrList.hasMoreItems = true;
    switch (this.component.componentType) {
      case PhasrPanelComponentTypes.BehavioralProfiles:
        const filters = this.getBehavioralProfilesDefaultFilters();
        filters.push({
            property: 'notUsingSuchTools',
            value: isFirstSearchInput
          },{
            property: 'behavioralProfileIdentityName',
            value: searchedValue
          },{
            property: 'behavioralProfileResourceName',
            value: searchedValue
          });
        await this.setBehavioralProfilesListItems(filters, true, isFirstSearchInput);
        break;
      case PhasrPanelComponentTypes.RestrictedBehavioralProfiles:
        const restrictedFilters = this.getBehavioralProfilesDefaultFilters();
        restrictedFilters.push({
          property: 'behavioralProfileIdentityName',
          value: searchedValue
        }, {
          property: 'behavioralProfileResourceName',
          value: searchedValue
        }, {
          property: 'mode',
          value: isFirstSearchInput ? Number(BehavioralProfileTypes.AUTOPILOT) : Number(BehavioralProfileTypes.DIRECT_CONTROL)
        });
        await this.setBehavioralProfilesListItems(restrictedFilters, true, isFirstSearchInput);
        break;
      case PhasrPanelComponentTypes.RuleTriggers:
        await this.setRuleTriggersListItems(phasrList, searchedValue);
        break;
      case PhasrPanelComponentTypes.BehavioralProfilesNoToggle:
        const recommendationFilters = this.getBehavioralProfilesDefaultFilters();
        recommendationFilters.push(
          {
            property: 'behavioralProfileIdentityName',
            value: searchedValue,
          },
          {
            property: 'behavioralProfileResourceName',
            value: searchedValue,
          }
        );
        await this.setBehavioralProfilesListItems(recommendationFilters, true, isFirstSearchInput);
        break;
    }
  }

  private getBehavioralProfilesDefaultFilters(): any[] {
    const defaultFilters = [
      {
        property: 'objectId',
        value: this.component.componentSpecificData.recommendationId,
      },
    ] as any[];
    switch (this.component.componentType) {
      case PhasrPanelComponentTypes.BehavioralProfiles:
        defaultFilters.push({
          property: 'collection',
          value: 'rules',
        }, {
          property: 'isRestricted',
          value: false
        });
        break;
      case PhasrPanelComponentTypes.BehavioralProfilesNoToggle:
        defaultFilters.push({
          property: 'collection',
          value: 'recommendations',
        });
        break;
      case PhasrPanelComponentTypes.RestrictedBehavioralProfiles:
        defaultFilters.push({
          property: 'collection',
          value: 'rules',
        },
        {
          "property": "isRestricted",
          "value": true
        });
        break;
      default:
        return [];
    }
    return defaultFilters;
  }

  private handleActionButton(): void {
    const dictionary = Application.Main.getPhasrDictionary();
    const data: PhasrApplyActionSchema = {
      id: this.component.componentSpecificData.recommendationId,
      companyId: this.component.companyId,
      ruleId: this.component.componentSpecificData.ruleId,
      description: dictionary[this.component.componentSpecificData.ruleId]?.rule_description ?? 'N/A',
      targetedActivityType: getPhasrCategoryDictionaryToPrefferedName()[dictionary[this.component.componentSpecificData.ruleId]?.['category']] ?? 'N/A',
      rule: this.component.componentSpecificData.ruleTriggers,
    };

    const event = new CustomEvent(PhasrSidePanelEvents.EditAccess, {
      detail: data,
      bubbles: true,
      composed: true,
    });

    this.component.dispatchEvent(event);
  }
}
